/**
 * Copy text, and say whether it worked. `navigator.clipboard` is secure-context
 * only, so a console on a plain-http LAN address has none — in every browser,
 * not just Safari. There the old select-and-copy path still works, as long as
 * it runs inside the tap: nothing before it may await.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Refused (permissions, lost focus): the fallback below might still work.
    }
  }
  return selectAndCopy(text);
}

function selectAndCopy(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  const area = document.createElement('textarea');
  area.value = text;
  // Read-only and off to the side, so iOS neither zooms nor raises a keyboard.
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '0';
  area.style.opacity = '0';
  area.style.fontSize = '16px';
  document.body.appendChild(area);
  try {
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
